import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV } from "../data/content";
import { scrollToId } from "../lib/scroll";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled || open ? "bg-[rgba(10,10,11,0.82)] backdrop-blur-xl border-b border-[var(--line)]" : "border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="nav-logo-link" onClick={() => setOpen(false)}>
          <img src="/images/logo-mark.png" alt="Salgin Solutions" className="h-8 w-auto transition-transform duration-500 group-hover:rotate-[8deg]" />
          <span className="hidden sm:flex flex-col leading-none">
            <span className="text-[0.95rem] font-semibold tracking-[0.18em] text-[var(--text)]">SALGIN SOLUTIONS</span>
            <span className="text-[0.55rem] tracking-[0.3em] text-[var(--gold)] mt-1 uppercase font-mono">KI-Bilder & Skizzen</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {NAV.map((n) => n.to ? (
            <Link key={n.id} to={n.to} className={`nav-link text-[0.72rem] xl:text-[0.8rem] tracking-[0.12em] uppercase whitespace-nowrap ${location.pathname.startsWith(n.to) ? "!text-[var(--gold)]" : ""}`} data-testid={`nav-link-${n.id}`}>
              {n.label}
            </Link>
          ) : (
            <button key={n.id} onClick={() => go(n.id)} className="nav-link text-[0.72rem] xl:text-[0.8rem] tracking-[0.12em] uppercase whitespace-nowrap" data-testid={`nav-link-${n.id}`}>
              {n.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-4">
          <button onClick={() => go("kontakt")} className="hidden md:inline-flex btn-gold !py-3 !px-5 text-xs" data-testid="nav-cta-button">
            Anfrage starten
          </button>
          <button className="lg:hidden text-[var(--text)] p-2" onClick={() => setOpen((v) => !v)} aria-label="Menü" data-testid="nav-mobile-toggle">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[rgba(10,10,11,0.95)] border-t border-[var(--line)]"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {NAV.map((n) => n.to ? (
                <Link key={n.id} to={n.to} onClick={() => setOpen(false)} className="text-left font-display text-2xl text-[var(--text)]" data-testid={`nav-mobile-link-${n.id}`}>
                  {n.label}
                </Link>
              ) : (
                <button key={n.id} onClick={() => go(n.id)} className="text-left font-display text-2xl text-[var(--text)]" data-testid={`nav-mobile-link-${n.id}`}>
                  {n.label}
                </button>
              ))}
              <button onClick={() => go("kontakt")} className="btn-gold mt-2 justify-center" data-testid="nav-mobile-cta">Anfrage starten</button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
